/**
 * @module lib/api
 * @summary Cloudflare Worker API client + session credential storage.
 * @description
 *   All functions are no-ops when VITE_WORKER_URL is not set, so the app
 *   works offline / locally with only localStorage. When the URL is set,
 *   the Worker becomes the source of truth and localStorage is a cache.
 *
 *   Credentials are stored in sessionStorage (cleared when the browser tab
 *   closes) so authenticated writes survive page refreshes within a session.
 */
import type { CatConfig, CustomSymbolRecord, Role, User } from '../types';

// The shared backend (Cloudflare Worker). The build-time env var wins when set,
// but we fall back to the known deployment URL so the app always reaches the
// backend even if the Pages build doesn't inject VITE_WORKER_URL. This URL is
// public (the browser calls it directly) — it is NOT a secret.
const DEFAULT_WORKER_URL = 'https://jcc-pfd-api.ibrahim-alosman.workers.dev';
const BASE: string =
  (import.meta.env.VITE_WORKER_URL as string | undefined)?.trim() || DEFAULT_WORKER_URL;

// ── Credential storage (sessionStorage, cleared on tab close) ─────────────────

const CREDS_KEY = 'jcc_api_creds';

interface Creds {
  username: string;
  password: string;
}

export function setApiCreds(username: string, password: string): void {
  sessionStorage.setItem(CREDS_KEY, btoa(JSON.stringify({ username, password })));
}

export function getApiCreds(): Creds | null {
  try {
    const v = sessionStorage.getItem(CREDS_KEY);
    return v ? (JSON.parse(atob(v)) as Creds) : null;
  } catch {
    return null;
  }
}

export function clearApiCreds(): void {
  sessionStorage.removeItem(CREDS_KEY);
}

function authHeader(creds: Creds): string {
  return 'Basic ' + btoa(`${creds.username}:${creds.password}`);
}

// ── Login ─────────────────────────────────────────────────────────────────────

/** Returns the user's role from the Worker, or null if credentials are wrong / Worker unavailable. */
export async function apiLogin(username: string, password: string): Promise<Role | null> {
  if (!BASE) return null;
  try {
    const r = await fetch(`${BASE}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!r.ok) return null;
    const { role } = (await r.json()) as { role: Role };
    return role;
  } catch {
    return null;
  }
}

// ── Symbols ───────────────────────────────────────────────────────────────────

export async function apiGetSymbols(): Promise<Record<string, CustomSymbolRecord>> {
  if (!BASE) return {};
  try {
    const r = await fetch(`${BASE}/api/symbols`);
    if (!r.ok) return {};
    return (await r.json()) as Record<string, CustomSymbolRecord>;
  } catch {
    return {};
  }
}

export async function apiSaveSymbols(
  symbols: Record<string, CustomSymbolRecord>,
  creds: Creds,
): Promise<boolean> {
  if (!BASE) return false;
  try {
    const r = await fetch(`${BASE}/api/symbols`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: authHeader(creds) },
      body: JSON.stringify(symbols),
    });
    return r.ok;
  } catch {
    return false;
  }
}

// ── Categories ────────────────────────────────────────────────────────────────

/**
 * Fetch the shared category arrangement. Returns null when the Worker is not
 * configured or has no data. Handles the legacy shape where only a `string[]`
 * of custom category names was stored.
 */
export async function apiGetCatConfig(): Promise<CatConfig | null> {
  if (!BASE) return null;
  try {
    const r = await fetch(`${BASE}/api/categories`);
    if (!r.ok) return null;
    const data = await r.json();
    if (Array.isArray(data)) {
      // Legacy: only custom names were stored.
      return { custom: data as string[], order: [], renames: {}, inactive: [], moves: {}, deleted: [] };
    }
    if (data && typeof data === 'object') return data as CatConfig;
    return null;
  } catch {
    return null;
  }
}

export async function apiSaveCatConfig(cfg: CatConfig, creds: Creds): Promise<boolean> {
  if (!BASE) return false;
  try {
    const r = await fetch(`${BASE}/api/categories`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: authHeader(creds) },
      body: JSON.stringify(cfg),
    });
    return r.ok;
  } catch {
    return false;
  }
}

// ── Users ─────────────────────────────────────────────────────────────────────

export async function apiGetUsers(creds: Creds): Promise<User[]> {
  if (!BASE) return [];
  try {
    const r = await fetch(`${BASE}/api/users`, {
      headers: { Authorization: authHeader(creds) },
    });
    if (!r.ok) return [];
    return (await r.json()) as User[];
  } catch {
    return [];
  }
}

/** Result of a targeted user operation: the error text, or the fresh list. */
export interface UserOpResult {
  error?: string;
  users?: User[];
}

/**
 * Run one atomic user operation on the Worker, which re-reads the stored list
 * before applying it. Returns `{}` when the Worker is unreachable so the caller
 * can fall back to the local cache.
 */
async function userOp(
  op: 'add' | 'delete' | 'password',
  body: Partial<User>,
  creds: Creds,
): Promise<UserOpResult> {
  if (!BASE) return {};
  try {
    const r = await fetch(`${BASE}/api/users/${op}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: authHeader(creds) },
      body: JSON.stringify(body),
    });
    const data = (await r.json()) as { error?: string; users?: User[] };
    if (!r.ok) return { error: data.error || 'Request failed.' };
    return { users: data.users };
  } catch {
    return {};
  }
}

export const apiAddUser = (u: User, creds: Creds) => userOp('add', u, creds);
export const apiDeleteUser = (username: string, creds: Creds) =>
  userOp('delete', { username }, creds);
export const apiSetPassword = (username: string, password: string, creds: Creds) =>
  userOp('password', { username, password }, creds);

export async function apiSaveUsers(users: User[], creds: Creds): Promise<boolean> {
  if (!BASE) return false;
  try {
    const r = await fetch(`${BASE}/api/users`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: authHeader(creds) },
      body: JSON.stringify(users),
    });
    return r.ok;
  } catch {
    return false;
  }
}
